import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts'
import { fetchHoldingDetail, type HoldingDetail } from '../lib/data'

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function formatPrice(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  })
}

type ChartTab = 'value' | 'price'
type TimeRange = '1mo' | '3mo' | '6mo' | '1y' | 'all'

// Buy/sell marker types from transactions
interface TradeMarker {
  date: string
  type: 'BUY' | 'SELL'
  quantity: number | null
  price_pence: number | null
}

function Holding() {
  const { portfolioId, ticker } = useParams<{ portfolioId: string; ticker: string }>()
  const [holding, setHolding] = useState<HoldingDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<ChartTab>('value')
  const [timeRange, setTimeRange] = useState<TimeRange>('1y')

  useEffect(() => {
    if (portfolioId && ticker) {
      loadHoldingData()
    }
  }, [portfolioId, ticker])

  async function loadHoldingData() {
    try {
      setLoading(true)
      const data = await fetchHoldingDetail(portfolioId!, ticker!)
      if (!data) {
        throw new Error('Holding not found')
      }
      setHolding(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load holding')
    } finally {
      setLoading(false)
    }
  }

  // Filter chart data by time range
  const getFilteredData = () => {
    if (!holding) return []
    
    const data = activeTab === 'value' ? holding.valueHistory : holding.priceHistory
    if (!data || data.length === 0) return []
    
    const now = new Date()
    const cutoffDate = new Date()
    
    switch (timeRange) {
      case '1mo':
        cutoffDate.setMonth(now.getMonth() - 1)
        break
      case '3mo':
        cutoffDate.setMonth(now.getMonth() - 3)
        break
      case '6mo':
        cutoffDate.setMonth(now.getMonth() - 6)
        break
      case '1y':
        cutoffDate.setFullYear(now.getFullYear() - 1)
        break
      case 'all':
      default:
        return data
    }
    
    return data.filter(d => new Date(d.date) >= cutoffDate)
  }

  const chartData = getFilteredData()

  // Build trade markers from transactions
  const tradeMarkers = useMemo(() => {
    if (!holding) return []
    const markers: TradeMarker[] = []
    for (const tx of holding.transactions) {
      if (tx.type === 'BUY' || tx.type === 'SELL' || tx.type === 'CARD_WEB') {
        markers.push({
          date: tx.trade_date,
          type: tx.type === 'SELL' ? 'SELL' : 'BUY',
          quantity: tx.quantity,
          price_pence: tx.price_pence,
        })
      }
    }
    return markers
  }, [holding])

  // Find y-value for each marker from chart data
  const markerPoints = useMemo(() => {
    const points: Array<{
      date: string
      x: string
      y: number
      type: 'BUY' | 'SELL'
      quantity: number | null
    }> = []

    for (const marker of tradeMarkers) {
      const date = marker.date
      // Find closest chart point on or after this date
      const chartPoint = chartData.find(d => d.date >= date)
      if (chartPoint) {
        const y = activeTab === 'value'
          ? (chartPoint as any).value ?? 0
          : (chartPoint as any).close ?? 0
        if (y > 0) {
          points.push({
            date,
            x: chartPoint.date,
            y,
            type: marker.type,
            quantity: marker.quantity,
          })
        }
      }
    }
    return points
  }, [tradeMarkers, chartData, activeTab])

  // Calculate YTD, MTD changes
  const calculatePeriodChange = (days: number): { value: number; pct: number } | null => {
    if (!holding || chartData.length < 2) return null
    
    const now = new Date()
    const cutoffDate = new Date()
    cutoffDate.setDate(now.getDate() - days)
    
    const recentData = chartData.filter(d => new Date(d.date) >= cutoffDate)
    if (recentData.length === 0) return null
    
    const current = activeTab === 'value' 
      ? holding.currentValue 
      : (holding.priceInPence || 0) / 100
    const firstData = recentData[0] as { date: string; close?: number; value?: number }
    const past = activeTab === 'value'
      ? (firstData.value || (firstData.close || 0) * holding.quantity / 100)
      : (firstData.close || 0) / 100
    
    const change = current - past
    const pct = past > 0 ? (change / past) * 100 : 0
    
    return { value: change, pct }
  }

  const dayChange = holding ? { value: holding.dayChange, pct: holding.dayChangePct } : null
  const mtdChange = calculatePeriodChange(30)
  const ytdChange = calculatePeriodChange(365)

  if (loading) {
    return (
      <div className="app-container">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  if (error || !holding) {
    return (
      <div className="app-container">
        <div className="flex items-center gap-2 mb-4">
          <Link to={`/portfolio/${portfolioId}`} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <h1 className="text-xl font-bold">Error</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error || 'Holding not found'}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="app-container">
      {/* Back button and title */}
      <div className="flex items-center gap-2 mb-4">
        <Link to={`/portfolio/${portfolioId}`} className="p-2 -ml-2 rounded-lg hover:bg-gray-100">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <h1 className="text-xl font-bold">{holding.name}</h1>
          <p className="text-sm text-gray-500 font-mono">{ticker}</p>
        </div>
      </div>

      {/* Holding Summary */}
      <div className="card mb-6">
        <p className="text-3xl font-bold text-gray-900">{formatCurrency(holding.currentValue)}</p>
        <div className="flex items-center gap-4 mt-2 text-sm">
          <span className="text-gray-500">Cost: {formatCurrency(holding.cost_basis)}</span>
          <span className={holding.gain >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
            Gain: {holding.gain >= 0 ? '+' : ''}{formatCurrency(holding.gain)} ({holding.gainPct >= 0 ? '+' : ''}{holding.gainPct.toFixed(1)}%)
          </span>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-600">
          <span>Units: {holding.quantity.toLocaleString()}</span>
          <span className="mx-2">•</span>
          <span>Avg cost: {formatCurrency(holding.cost_basis / holding.quantity)}</span>
        </div>
      </div>

      {/* Period Changes - Show both £ and % */}
      <div className="grid grid-cols-3 gap-2 mb-6">
        <div className="card py-3 text-center">
          <p className="text-xs text-gray-500 mb-1">Today</p>
          {dayChange ? (
            <div className={dayChange.value >= 0 ? 'text-green-600' : 'text-red-600'}>
              <p className="font-semibold">{formatCurrency(Math.abs(dayChange.value))}</p>
              <p className="text-xs">{dayChange.value >= 0 ? '+' : ''}{dayChange.pct.toFixed(1)}%</p>
            </div>
          ) : (
            <p className="text-gray-400">-</p>
          )}
        </div>
        <div className="card py-3 text-center">
          <p className="text-xs text-gray-500 mb-1">MTD</p>
          {mtdChange ? (
            <div className={mtdChange.value >= 0 ? 'text-green-600' : 'text-red-600'}>
              <p className="font-semibold">{formatCurrency(Math.abs(mtdChange.value))}</p>
              <p className="text-xs">{mtdChange.value >= 0 ? '+' : ''}{mtdChange.pct.toFixed(1)}%</p>
            </div>
          ) : (
            <p className="text-gray-400">-</p>
          )}
        </div>
        <div className="card py-3 text-center">
          <p className="text-xs text-gray-500 mb-1">YTD</p>
          {ytdChange ? (
            <div className={ytdChange.value >= 0 ? 'text-green-600' : 'text-red-600'}>
              <p className="font-semibold">{formatCurrency(Math.abs(ytdChange.value))}</p>
              <p className="text-xs">{ytdChange.value >= 0 ? '+' : ''}{ytdChange.pct.toFixed(1)}%</p>
            </div>
          ) : (
            <p className="text-gray-400">-</p>
          )}
        </div>
      </div>

      {/* Chart */}
      <div className="card mb-6">
        {/* Chart Tabs */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setActiveTab('value')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeTab === 'value'
                  ? 'bg-white text-primary-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Value
            </button>
            <button
              onClick={() => setActiveTab('price')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                activeTab === 'price'
                  ? 'bg-white text-primary-700 font-medium shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Price
            </button>
          </div>
        </div>

        {/* Chart with buy/sell markers */}
        <div className="h-52">
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-GB', { month: 'short' })}
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                />
                <YAxis
                  tickFormatter={(value) => 
                    activeTab === 'value'
                      ? `£${(value / 1000).toFixed(1)}k`
                      : `£${value.toFixed(0)}`
                  }
                  tick={{ fontSize: 11 }}
                  stroke="#9ca3af"
                  width={50}
                  domain={['auto', 'auto']}
                />
                <Tooltip
                  formatter={(value) =>
                    formatCurrency(value as number)
                  }
                  labelFormatter={(label) => formatDate(label as string)}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    fontSize: '12px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={activeTab === 'value' ? 'value' : 'close'}
                  stroke="#4f46e5"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                {/* Buy/sell markers */}
                {markerPoints.map((marker, idx) => (
                  <ReferenceDot
                    key={`marker-${idx}`}
                    x={marker.x}
                    y={marker.y}
                    r={5}
                    fill={marker.type === 'BUY' ? '#10b981' : '#ef4444'}
                    stroke="white"
                    strokeWidth={2}
                    label={{
                      value: marker.type === 'BUY' ? 'B' : 'S',
                      position: 'top',
                      fontSize: 9,
                      fontWeight: 'bold',
                      fill: marker.type === 'BUY' ? '#10b981' : '#ef4444',
                    }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full bg-gray-50 rounded-lg flex items-center justify-center">
              <span className="text-gray-400">No chart data available</span>
            </div>
          )}
        </div>

        {/* Legend for markers */}
        {markerPoints.length > 0 && (
          <div className="flex justify-center gap-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block"></span> Buy
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span> Sell
            </span>
          </div>
        )}

        {/* Time range selector */}
        <div className="flex justify-center gap-2 mt-3">
          {(['1mo', '3mo', '6mo', '1y', 'all'] as TimeRange[]).map((period) => (
            <button
              key={period}
              onClick={() => setTimeRange(period)}
              className={`text-xs px-3 py-1 rounded-full transition-colors ${
                timeRange === period
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {period === 'all' ? 'ALL' : period.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">📋 Transactions ({holding.transactions.length})</h2>
        <div className="space-y-2">
          {holding.transactions.map((tx) => (
            <div key={tx.id} className="card py-3 flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{formatDate(tx.trade_date)}</p>
                <p className="font-medium">
                  {tx.type} {tx.quantity ? `${tx.quantity} units` : ''} 
                  {tx.price_pence ? `@ ${formatPrice(tx.price_pence)}` : ''}
                </p>
                {tx.description && (
                  <p className="text-xs text-gray-500 truncate max-w-[200px]">{tx.description}</p>
                )}
              </div>
              <span className={`font-medium ${tx.value_gbp >= 0 ? 'text-green-600' : 'text-gray-900'}`}>
                {tx.value_gbp >= 0 ? '+' : ''}{formatCurrency(tx.value_gbp)}
              </span>
            </div>
          ))}
        </div>
        {holding.transactions.length === 0 && (
          <p className="text-center text-gray-500 py-4">No transactions found</p>
        )}
      </div>
    </div>
  )
}

export default Holding
